import { SmallTitle } from '@sleepstation/components'
import { VLayout } from '@sleepstation/components-layouts'
import { LAYOUT_SPACING } from '@sleepstation/const'
import { parseToAst } from '@sleepstation/markdown'
import { replaceTokens, lensById } from '@sleepstation/utils'
import PropTypes from 'prop-types'
import {
  always,
  assoc,
  clone,
  compose,
  find,
  ifElse,
  lensPath,
  lensProp,
  propEq,
  mergeDeepRight,
  reject,
  set,
  identity,
  view,
} from 'ramda'
import React, { useState, useContext } from 'react'
import styled from 'styled-components'
import { RANGE_EPWORTH } from '../../../../const/epworth'
import NOTICE_TYPE from '../../../../const/noticeType'
import BreakoutModuleMid from '../../../shared/breakoutModules/BreakoutModuleMid'
import HeaderAttachedBreakoutModule from '../../../shared/breakoutModules/HeaderAttachedBreakoutModule'
import { getRangeEpworth } from '../../../../utils/epworth'
import HubSectionsList from './HubSectionsList'
import HeaderAttachedBannerContent from './HeaderAttachedBannerContent'
import Notice from './Notice'
import RoutingContext from '../../../../context/RoutingContext'

// -----------------------------------------------------------------------------
// Const
// -----------------------------------------------------------------------------

const EPWORTH_SCORE_BACKGROUND_COLOURS = {
  [RANGE_EPWORTH.LOW]: `positive`,
  [RANGE_EPWORTH.MILD]: `neutral`,
  [RANGE_EPWORTH.MODERATE]: `neutral`,
  [RANGE_EPWORTH.HIGH]: `negative`,
}

// -----------------------------------------------------------------------------
// Utils
// -----------------------------------------------------------------------------

const getNotice = ({ chosenName, content }) => {
  const notice = content.notices[NOTICE_TYPE.REJECTED]

  const noticeBody = replaceTokens(notice.bodyTemplate, {
    chosenName,
  })

  const noticeBodyAst = parseToAst(noticeBody)

  return assoc(`bodyAst`, noticeBodyAst, notice)
}

const addEpworthData = (aboutYourSleepSection, latestEpworthResult) => {
  const epworthCard = find(
    propEq(`are-you-getting-enough-sleep`, `id`),
    aboutYourSleepSection.cards
  )
  const range = getRangeEpworth(latestEpworthResult.result)
  epworthCard.badge = mergeDeepRight(latestEpworthResult, {
    backgroundColour: EPWORTH_SCORE_BACKGROUND_COLOURS[range],
    showBadge: true,
  })
  epworthCard.link = epworthCard.resultsLink
}

const addDbasData = (aboutYourSleepSection, latestDbasResult) => {
  const dbasCard = find(
    propEq(`what-do-you-know-about-sleep`, `id`),
    aboutYourSleepSection.cards
  )
  dbasCard.badge = mergeDeepRight(latestDbasResult, {
    backgroundColour: `generic`,
    showBadge: true,
  })
  dbasCard.link = dbasCard.resultsLink
}

const transformAboutYourSleepSection = (
  pageSections,
  currentEnrolmentQuestionnaires
) => {
  const { latestEpworthResult, latestDbasResult } =
    currentEnrolmentQuestionnaires
  const transformedPageSections = clone(pageSections)
  const aboutYourSleepSection = find(
    propEq(`about-your-sleep`, `id`),
    transformedPageSections
  )

  if (latestDbasResult) {
    addDbasData(aboutYourSleepSection, latestDbasResult)
  }
  if (latestEpworthResult) {
    addEpworthData(aboutYourSleepSection, latestEpworthResult)
  }

  return transformedPageSections
}

// NEW FUNCTIONALITY TO REMOVE CARD

const isNeedHelpSection = propEq(`need-help`, `id`)

const rejectNeedHelpSection = reject(isNeedHelpSection)

const modifySectionsBasedOnUserState = (willUserBeRejected) =>
  ifElse(always(willUserBeRejected), rejectNeedHelpSection, identity)

const setBodyCopy = set(
  compose(
    lensById(`need-help`),
    lensProp(`cards`),
    lensById(`sleep-improvement-programme`),
    lensPath([`primary`, `bodyAst`])
  )
)

const setLinkUrl = set(
  compose(
    lensById(`need-help`),
    lensProp(`cards`),
    lensById(`sleep-improvement-programme`),
    lensPath([`link`, `url`])
  )
)

const setLinkPrologue = set(
  compose(
    lensById(`need-help`),
    lensProp(`cards`),
    lensById(`sleep-improvement-programme`),
    lensPath([`link`, `prologue`])
  )
)

const setShouldShowNhsLogo = set(
  compose(
    lensById(`need-help`),
    lensProp(`cards`),
    lensById(`sleep-improvement-programme`),
    lensProp(`shouldShowNhsLogo`)
  )
)

const viewPrivateGrantBody = view(
  compose(
    lensById(`need-help`),
    lensProp(`cards`),
    lensById(`sleep-improvement-programme`),
    lensPath([`primary`, `bodyAstPrivateGrant`])
  )
)

const viewNhsLinkUrl = view(
  compose(
    lensById(`need-help`),
    lensProp(`cards`),
    lensById(`sleep-improvement-programme`),
    lensPath([`linkNhs`, `url`])
  )
)

const viewPrivateLinkUrl = view(
  compose(
    lensById(`need-help`),
    lensProp(`cards`),
    lensById(`sleep-improvement-programme`),
    lensPath([`linkPrivate`, `url`])
  )
)

const viewPrivateLinkUrlWithAccess = view(
  compose(
    lensById(`need-help`),
    lensProp(`cards`),
    lensById(`sleep-improvement-programme`),
    lensPath([`linkPrivateWithAccess`, `url`])
  )
)

const viewPrivateGrantLinkUrl = view(
  compose(
    lensById(`need-help`),
    lensProp(`cards`),
    lensById(`sleep-improvement-programme`),
    lensPath([`linkPrivateGrant`, `url`])
  )
)

const viewPrivateGrantLinkPrologue = view(
  compose(
    lensById(`need-help`),
    lensProp(`cards`),
    lensById(`sleep-improvement-programme`),
    lensPath([`linkPrivateGrant`, `prologue`])
  )
)

const setStartTherapyAfterReferralRequestLinkUrl = set(
  compose(lensPath([`link`, `url`]))
)

const modifyReferralRequestBannerContent = (appRouting, content) =>
  setStartTherapyAfterReferralRequestLinkUrl(appRouting.assessmentPath, content)

const modifySectionContentBasedOnEnrolmentCategory = (
  enrolmentCategoryType,
  sections,
  willUserBeRejected,
  enrolmentCanSubmitReferralRequest,
  canAccessProgramme,
  enrolmentHasOpenReferralRequest,
  licensorPrefersSelfFunding
) => {
  if (willUserBeRejected) {
    return sections
  }

  if (
    enrolmentCategoryType !== `PRIVATE` &&
    enrolmentCategoryType !== `FREE` &&
    enrolmentCategoryType !== `NHS` &&
    enrolmentCategoryType !== `CORPORATE`
  ) {
    return sections
  }

  if (enrolmentCategoryType === `CORPORATE`) {
    if (licensorPrefersSelfFunding === true) {
      const privateLinkUrl = viewPrivateLinkUrl(sections)
      const sectionsWithModifiedContent = setLinkUrl(privateLinkUrl, sections)
      return sectionsWithModifiedContent
    }
    return sections
  }

  if (enrolmentCategoryType === `NHS`) {
    if (canAccessProgramme || enrolmentHasOpenReferralRequest) {
      const sectionsWithModifiedContent = reject(
        propEq(`need-help`, `id`),
        sections
      )

      return sectionsWithModifiedContent
    }

    if (enrolmentCanSubmitReferralRequest) {
      const nhsLinkUrl = viewNhsLinkUrl(sections)
      const sectionsWithNhsBranding = setShouldShowNhsLogo(true, sections)
      const sectionsWithModifiedContent = setLinkUrl(
        nhsLinkUrl,
        sectionsWithNhsBranding
      )

      return sectionsWithModifiedContent
    }

    const privateGrantBody = viewPrivateGrantBody(sections)
    const privateGrantLinkUrl = viewPrivateGrantLinkUrl(sections)
    const privateGrantLinkPrologue = viewPrivateGrantLinkPrologue(sections)
    const sectionsWithModifiedBody = setBodyCopy(privateGrantBody, sections)
    const sectionsWithModifiedLink = setLinkUrl(
      privateGrantLinkUrl,
      sectionsWithModifiedBody
    )
    const sectionsWithModifiedContent = setLinkPrologue(
      privateGrantLinkPrologue,
      sectionsWithModifiedLink
    )

    return sectionsWithModifiedContent
  }

  if (canAccessProgramme) {
    const privateLinkUrl = viewPrivateLinkUrlWithAccess(sections)
    const sectionsWithModifiedContent = setLinkUrl(privateLinkUrl, sections)

    return sectionsWithModifiedContent
  }

  const privateLinkUrl = viewPrivateLinkUrl(sections)
  const sectionsWithModifiedContent = setLinkUrl(privateLinkUrl, sections)

  return sectionsWithModifiedContent
}

const modifySectionContentBasedOnReferralRequestStatus = (
  sections,
  enrolmentHasOpenReferralRequest,
  canAccessProgramme
) => {
  if (enrolmentHasOpenReferralRequest && !canAccessProgramme) {
    return sections
  }

  const sectionsWithModifiedContent = reject(propEq(`access`, `id`), sections)

  return sectionsWithModifiedContent
}

// -----------------------------------------------------------------------------
// Sub-components
// -----------------------------------------------------------------------------

const Layout = styled(VLayout).attrs({
  spacing: [LAYOUT_SPACING.LARGE, LAYOUT_SPACING.LARGE, LAYOUT_SPACING.X_LARGE],
})``

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

const HubPage = ({
  content,
  currentEnrolmentQuestionnaires,
  enrolmentCategoryType,
  currentUser,
  onNoticeClose,
  isNoticeActive,
  canAccessProgramme,
  willUserBeRejected,
  enrolmentCanSubmitReferralRequest,
  enrolmentHasOpenReferralRequest,
  enrolmentHasReferralRequest,
  licensorPrefersSelfFunding,
}) => {
  const { appRouting } = useContext(RoutingContext)
  const { chosenName } = currentUser
  const [shouldShowNotice, setShouldShowNotice] = useState(isNoticeActive)
  const canViewReferralRequestProcessedBanner =
    canAccessProgramme && enrolmentHasReferralRequest

  const modifiedReferralRequestBannerContent =
    modifyReferralRequestBannerContent(
      appRouting,
      content.referralRequestProcessedBanner
    )

  const transformedPageSections = transformAboutYourSleepSection(
    content.sections,
    currentEnrolmentQuestionnaires
  )

  const modifiedSections = modifySectionsBasedOnUserState(
    willUserBeRejected,
    enrolmentCanSubmitReferralRequest
  )(transformedPageSections)

  const sectionsWithModifiedContent =
    modifySectionContentBasedOnEnrolmentCategory(
      enrolmentCategoryType,
      modifiedSections,
      willUserBeRejected,
      enrolmentCanSubmitReferralRequest,
      canAccessProgramme,
      enrolmentHasOpenReferralRequest,
      licensorPrefersSelfFunding
    )

  const sectionsWithModifiedContentForReferralRequest =
    modifySectionContentBasedOnReferralRequestStatus(
      sectionsWithModifiedContent,
      enrolmentHasOpenReferralRequest,
      canAccessProgramme,
      licensorPrefersSelfFunding
    )

  const handleNoticeClose = () => {
    setShouldShowNotice(false)
    onNoticeClose()
  }

  const notice = shouldShowNotice && getNotice({ chosenName, content })

  const welcomeMessage = replaceTokens(content.welcomeMessageTemplate, {
    chosenName,
  })

  return (
    <React.Fragment>
      {canViewReferralRequestProcessedBanner && (
        <HeaderAttachedBreakoutModule showBottomBorder={false}>
          <SmallTitle>{welcomeMessage}</SmallTitle>
          <HeaderAttachedBannerContent
            id="referral-request-processed"
            title={content.referralRequestProcessedBanner.title}
            bodyAst={content.referralRequestProcessedBanner.bodyAst}
            link={modifiedReferralRequestBannerContent.link}
          />
        </HeaderAttachedBreakoutModule>
      )}
      <BreakoutModuleMid
        hasBottomBorder={false}
        hasGradient={false}
        marginV={[
          [1.5, 2],
          [1.5, 3],
          [2.5, 4],
        ]}
      >
        <Layout>
          {!canViewReferralRequestProcessedBanner && (
            <SmallTitle>{welcomeMessage}</SmallTitle>
          )}
          {shouldShowNotice && (
            <Notice
              title={notice.title}
              bodyAst={notice.bodyAst}
              onClose={handleNoticeClose}
            />
          )}
          <HubSectionsList
            sections={sectionsWithModifiedContentForReferralRequest}
          />
        </Layout>
      </BreakoutModuleMid>
    </React.Fragment>
  )
}

HubPage.propTypes = {
  content: PropTypes.object.isRequired,
  currentUser: PropTypes.object.isRequired,
  currentEnrolmentQuestionnaires: PropTypes.object.isRequired,
  onNoticeClose: PropTypes.func.isRequired,
  isNoticeActive: PropTypes.bool.isRequired,
  canAccessProgramme: PropTypes.bool.isRequired,
  willUserBeRejected: PropTypes.bool.isRequired,
  enrolmentCanSubmitReferralRequest: PropTypes.bool.isRequired,
  enrolmentHasOpenReferralRequest: PropTypes.bool.isRequired,
  enrolmentCategoryType: PropTypes.string.isRequired,
  enrolmentHasReferralRequest: PropTypes.bool.isRequired,
  licensorPrefersSelfFunding: PropTypes.bool.isRequired,
}

export default HubPage
